import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { EinField, PhoneField, ZipField } from "@/components/common/masked-form-field";
import { CheckboxField, SelectField } from "@/features/clients/components/record-form-fields";
import { createEmployer, updateEmployer } from "@/features/worksheets/api";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import { PAY_PERIOD_OPTIONS, type Employer } from "@/features/worksheets/types";

interface Values {
  name: string;
  occupation: string;
  ein: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  payPeriod: string;
  grossWages: string;
  federalTax: string;
  stateTax: string;
  localTax: string;
  noFica: boolean;
  noMedicare: boolean;
}

const money = Yup.string().matches(/^\d*(\.\d{0,2})?$/, "Enter a dollar amount");
const schema = Yup.object({
  name: Yup.string().trim().required("Employer name is required"),
  grossWages: money,
  federalTax: money,
  stateTax: money,
  localTax: money,
});

interface Props {
  clientId: string;
  /** Preset TAXPAYER/SPOUSE from the section this dialog belongs to. */
  belongsTo: "TAXPAYER" | "SPOUSE";
  record: Employer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function EmployerDialog({ clientId, belongsTo, record, open, onOpenChange, onSaved }: Props) {
  const initialValues: Values = {
    name: record?.name ?? "",
    occupation: record?.occupation ?? "",
    ein: "",
    phone: record?.phone ?? "",
    street: record?.street ?? "",
    city: record?.city ?? "",
    state: record?.state ?? "",
    zip: record?.zip ?? "",
    payPeriod: record?.payPeriod ?? "",
    // Employer wages are decimal strings on the clients API (not cents).
    grossWages: record?.grossWages ?? "",
    federalTax: record?.federalTax ?? "",
    stateTax: record?.stateTax ?? "",
    localTax: record?.localTax ?? "",
    noFica: record?.noFica ?? false,
    noMedicare: record?.noMedicare ?? false,
  };

  async function onSave(v: Values) {
    const payload: Record<string, unknown> = {
      belongsTo,
      name: v.name.trim(),
      occupation: v.occupation,
      phone: v.phone,
      street: v.street,
      city: v.city,
      state: v.state,
      zip: v.zip,
      payPeriod: v.payPeriod || "",
      grossWages: v.grossWages || "0",
      federalTax: v.federalTax || "0",
      stateTax: v.stateTax || "0",
      localTax: v.localTax || "0",
      noFica: v.noFica,
      noMedicare: v.noMedicare,
    };
    if (v.ein.trim()) payload.ein = v.ein;
    if (record) await updateEmployer(clientId, record.id, payload);
    else await createEmployer(clientId, payload);
  }

  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Employer"
      initialValues={initialValues}
      validationSchema={schema}
      onSave={onSave}
      onSaved={onSaved}
      className="sm:max-w-2xl"
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField name="name" label="Employer" />
        <FormField name="occupation" label="Occupation" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <EinField
          name="ein"
          label="EIN"
          labelAdornment={
            record?.einLabel ? (
              <span className="text-muted-foreground text-xs">On file: {record.einLabel}</span>
            ) : undefined
          }
        />
        <PhoneField name="phone" label="Phone" />
      </div>
      <FormField name="street" label="Street" />
      <div className="grid grid-cols-3 gap-4">
        <FormField name="city" label="City" />
        <FormField name="state" label="State" maxLength={2} />
        <ZipField name="zip" label="ZIP" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <SelectField name="payPeriod" label="Pay period" options={PAY_PERIOD_OPTIONS} />
        <FormField name="grossWages" label="Gross wages ($/period)" inputMode="decimal" placeholder="0.00" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <FormField name="federalTax" label="Federal tax" inputMode="decimal" placeholder="0.00" />
        <FormField name="stateTax" label="State tax" inputMode="decimal" placeholder="0.00" />
        <FormField name="localTax" label="Local tax" inputMode="decimal" placeholder="0.00" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <CheckboxField name="noFica" label="Exempt from Social Security" />
        <CheckboxField name="noMedicare" label="Exempt from Medicare" />
      </div>
    </CrudDialog>
  );
}
